import { useState, useEffect, useCallback, useRef } from "react";

// react-router-dom components
import { Route, Routes, useLocation, useNavigate, matchPath } from "react-router-dom";
import api from "api/api";

import AuthService from "lib/Auth";
import routes from "routes";

import { useDispatch, useSelector } from "react-redux";
import { loadRoleState, loadUserAdminState, loadIdState } from "store/features/userSlice";
import { loadFiltersState, loadIsLoading } from "store/features/consumerFilterSlice";
import { clearTextThemeSelections } from "store/features/textThemeSlice";

import GlobalSnackbar from "./GlobalSnackbar";
import FullscreenLoader from "./FullscreenLoader";
const IDLE_CUTOFF_MS = 4.5 * 60 * 1000;
const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;

function AppContent() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const lastTrackRef = useRef({ path: null, time: 0 });
    const lastActivityRef = useRef(Date.now());

    const selectedStudies = useSelector((state) => state.studyDataStore.selectedStudies);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    const studyType = selectedStudies.length > 0 ? [...new Set(selectedStudies.map((ele) => ele.study_type))].join(", ") : null;
    const studyTypeMethod = selectedStudies.length > 0 ? [...new Set(selectedStudies.map((ele) => ele.study_type_method))].join(", ") : null;

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isAuthenticated && !productCategoryId && location.pathname !== "/text-theme-analysis") {
            navigate("/", { replace: true });
        }
    }, [isAuthenticated, location.pathname, productCategoryId, navigate]);

    useEffect(() => {
        const PRESERVE_SELECTION_PATHS = ["/text-theme-analysis", "/respondent-module"];
        if (!PRESERVE_SELECTION_PATHS.includes(location.pathname)) {
            dispatch(clearTextThemeSelections());
        }
    }, [dispatch, location.pathname]);

    // 🔽 keep ALL your existing effects & logic here
    // token refresh
    // fetchUser
    // tracking
    // consumer filters
    // renderContent()
    /* ---------------- ACTIVITY TRACKING (for idle detection) ---------------- */
    useEffect(() => {
        const markActive = () => { lastActivityRef.current = Date.now(); };
        const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];
        events.forEach((e) => window.addEventListener(e, markActive, { passive: true }));
        return () => events.forEach((e) => window.removeEventListener(e, markActive));
    }, []);

    /* ---------------- TOKEN AUTO REFRESH ---------------- */
    useEffect(() => {
        AuthService.refreshToken();

        const interval = setInterval(() => {
            AuthService.refreshToken();
        }, 60000);

        return () => clearInterval(interval);
    }, []);

    /* ---------------- LOAD USER ---------------- */

    useEffect(() => {
        const fetchUser = (retry = false) => {
            setIsLoading(true);

            api.get(`${process.env.REACT_APP_SERVER_PATH}/profiles/me/`, AuthService.getAuthHeader())
                .then((res) => {
                    dispatch(loadRoleState(res?.data?.role || null));
                    dispatch(loadUserAdminState(res?.data?.user_admin || false));
                    dispatch(loadIdState(res?.data?.id || null));

                    setIsAuthenticated(true);
                })
                .catch((error) => {
                    if (!retry) {
                        AuthService.refreshToken().then(() => fetchUser(true));
                    } else {
                        console.error("Authentication failed", error);
                        setIsAuthenticated(false);
                    }
                })
                .finally(() => setIsLoading(false));
        };

        fetchUser();
    }, [dispatch]);

    /* ---------------- PAGE TRACKING ---------------- */

    useEffect(() => {
        const track = (_productCategoryId, isHeartbeat = false) => {
            if (isHeartbeat && Date.now() - lastActivityRef.current > IDLE_CUTOFF_MS) return;

            let pageName = null;
            const pageData = [...routes]
                .sort((a, b) => b.path.length - a.path.length)
                .find((route) => matchPath({ path: route.path, end: true }, window.location.pathname));

            if (pageData) pageName = pageData.pageName;
            else if (window.location.pathname === "/") pageName = "Home";

            if (!pageName) return;

            const currentPath = window.location.pathname;
            const now = Date.now();
            const _coreDataModelIds = selectedStudies.map((s) => s.id);
            if (lastTrackRef.current.path === currentPath && now - lastTrackRef.current.time < 1000) {
                return;
            }
            lastTrackRef.current = { path: currentPath, time: now };

            api.post(`${process.env.REACT_APP_SERVER_PATH}/track/`, { request_ip: "", page: pageName, product_category: _productCategoryId,  core_data_model: _coreDataModelIds, }, AuthService.getAuthHeader()).catch((err) => console.error("Tracking error", err));
        };

        lastActivityRef.current = Date.now();
        track(productCategoryId);

        const interval = setInterval(() => track(productCategoryId, true), HEARTBEAT_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [location.pathname, productCategoryId]);

    /* ---------------- CONSUMER FILTERS ---------------- */

    const [cancelTokenDynamicFilter, setCancelTokenDynamicFilter] = useState(null);

    const fetchConsumerFilter = useCallback(() => {
        if (cancelTokenDynamicFilter) cancelTokenDynamicFilter.cancel();

        if (!selectedStudies.length) return;

        const source = api.CancelToken.source();
        setCancelTokenDynamicFilter(source);

        const baseUrl =
            `${process.env.REACT_APP_SERVER_PATH}/data-mappings/dynamic-consumer-filters/?archive=false&uniq=true&combined=false&variable_category=${encodeURIComponent("Consumer Filter,Study Filter,Consumer Filter related to Product Trial")}` +
            (productCategoryId ? `&product_category=${productCategoryId}` : "") +
            (studyType ? `&study_type=${studyType}` : "") +
            (studyTypeMethod ? `&study_type_method=${studyTypeMethod}` : "");

        const requests = selectedStudies.map((study) =>
            api.get(`${baseUrl}&core_data_model=${study.id}`, {
                cancelToken: source.token,
                ...AuthService.getAuthHeader(),
            })
        );

        dispatch(loadIsLoading(true));
        api.all(requests)
            .then((responses) => {
                const data = responses.flatMap((r) => r.data);
                dispatch(loadFiltersState(data));
            })
            .catch((err) => {
                if (!api.isCancel(err)) console.error("Filter error", err);
            })
            .finally(() => dispatch(loadIsLoading(false)));
    }, [dispatch, selectedStudies, productCategoryId]);

    useEffect(() => {
        fetchConsumerFilter();
    }, [fetchConsumerFilter]);

    /* ---------------- RENDER ---------------- */
    const getRoutes = (routesData) => {
        const comp = routesData.map((route, i) => {
            if (route.path) {
                return (
                    <Route
                        exact
                        path={route.path}
                        element={route.element}
                        key={i}
                    />
                );
            }

            return null;
        });
        return comp;
    };

    const renderContent = () => {
        if (isLoading) {
            return <FullscreenLoader />;
        }

        if (!isAuthenticated) {
            return <Routes>{getRoutes(routes.filter((datum) => !datum.isProtected))}</Routes>;
        }

        return (
            <>
                <Routes>{getRoutes(routes.filter((datum) => datum.isProtected))}</Routes>
                <GlobalSnackbar />
            </>
        );
    };

    return <div className="App">{renderContent()}</div>;
}

export default AppContent;
